import { z } from 'zod'
import { EventPriceKind } from '../enums'

/**
 * Event price info, enforcing constraints between kind and amount fields:
 *
 * - `free` / `unknown` → all price fields must be null/absent
 * - `fixed` → `priceFixed > 0`; range fields must be null
 * - `range` → `priceFixed` null; `priceRangeMin > 0`; `priceRangeMax > priceRangeMin`
 */
export const eventPriceInfoSchema = z
  .strictObject({
    kind: z.enum([
      EventPriceKind.UNKNOWN,
      EventPriceKind.FREE,
      EventPriceKind.FIXED,
      EventPriceKind.RANGE,
    ]),
    priceFixed: z.number().nullable().optional(),
    priceRangeMin: z.number().nullable().optional(),
    priceRangeMax: z.number().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.kind === EventPriceKind.FREE || data.kind === EventPriceKind.UNKNOWN) {
      if (data.priceFixed != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceFixed'],
          message: `priceFixed must be null when kind is ${data.kind}`,
        })
      }
      if (data.priceRangeMin != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceRangeMin'],
          message: `priceRangeMin must be null when kind is ${data.kind}`,
        })
      }
      if (data.priceRangeMax != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceRangeMax'],
          message: `priceRangeMax must be null when kind is ${data.kind}`,
        })
      }
      return
    }

    if (data.kind === EventPriceKind.FIXED) {
      if (data.priceFixed == null || data.priceFixed <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceFixed'],
          message: 'priceFixed must be greater than 0 when kind is fixed',
        })
      }
      if (data.priceRangeMin != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceRangeMin'],
          message: 'priceRangeMin must be null when kind is fixed',
        })
      }
      if (data.priceRangeMax != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['priceRangeMax'],
          message: 'priceRangeMax must be null when kind is fixed',
        })
      }
      return
    }

    if (data.priceFixed != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceFixed'],
        message: 'priceFixed must be null when kind is range',
      })
    }
    if (data.priceRangeMin == null || data.priceRangeMin <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceRangeMin'],
        message: 'priceRangeMin must be greater than 0 when kind is range',
      })
    }
    if (data.priceRangeMax == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceRangeMax'],
        message: 'priceRangeMax is required when kind is range',
      })
    } else if (data.priceRangeMin != null && data.priceRangeMax <= data.priceRangeMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceRangeMax'],
        message: 'priceRangeMax must be greater than priceRangeMin',
      })
    }
  })

export type EventPriceInfoInput = z.infer<typeof eventPriceInfoSchema>
